// SUB-OFFICE 7 — the behavior-log recorder. RAW events only, never computed
// results: the client records what happened and when; the server recomputes
// everything from seed + timestamps. The shared validator (physlog.ts) is run
// before submission so an honest log is never rejected for shape.

import { validateEnvelope, LOG_VERSION, type BehaviorLog, type LogEvent, type RouteData } from '../../shared/physlog';
import type { Player } from '../entities/player';

export class PhysLogRecorder {
  private t0 = Date.now();
  private perf0 = performance.now();
  private events: LogEvent[] = [];
  private lastT = -1;
  private routePts: Array<[number, number, number]> = [];
  private routeTimer: number | null = null;
  private stillMs = 0;
  private lastPt: [number, number] | null = null;

  constructor(private system: string, private taskId: string | null) {}

  /** Milliseconds since the recorder began (strictly monotonic when logged). */
  now(): number {
    return Math.round(performance.now() - this.perf0);
  }

  /** Record a raw event. Timestamps are forced strictly increasing. */
  ev(e: string, data: Record<string, unknown> = {}): void {
    const t = Math.max(this.now(), this.lastT + 1);
    this.lastT = t;
    this.events.push({ t, e, ...data });
  }

  /**
   * Route sampling at `intervalMs` (250ms base per the envelope contract),
   * 0.25m quantized. `transform` maps world coords into the space the server
   * verifies against (33-C logs in waypoint space).
   */
  startRoute(
    player: Player,
    intervalMs = 250,
    transform: (x: number, z: number) => [number, number] = (x, z) => [x, z]
  ): void {
    this.stopRoute();
    this.routeTimer = window.setInterval(() => {
      const [x, y] = transform(player.pos.x, player.pos.z);
      const qx = Math.round(x / 0.25) * 0.25;
      const qy = Math.round(y / 0.25) * 0.25;
      const t = Math.max(this.now(), (this.routePts.length ? this.routePts[this.routePts.length - 1][2] : -1) + 1);
      if (this.lastPt && this.lastPt[0] === qx && this.lastPt[1] === qy) this.stillMs += intervalMs;
      this.lastPt = [qx, qy];
      this.routePts.push([qx, qy, t]);
      if (this.routePts.length > 2380) this.stopRoute(); // stay under the cap
    }, intervalMs);
  }

  stopRoute(): void {
    if (this.routeTimer !== null) {
      window.clearInterval(this.routeTimer);
      this.routeTimer = null;
    }
  }

  /** Assemble the envelope; validates client-side (honesty check, not trust). */
  build(withRoute: boolean): { log: BehaviorLog; valid: boolean; reason?: string } {
    const route: RouteData | undefined = withRoute && this.routePts.length >= 2
      ? {
          pts: this.routePts,
          len_m: Math.round(this.routeLen() * 10) / 10,
          still_ms: this.stillMs,
        }
      : undefined;
    const log: BehaviorLog = {
      v: LOG_VERSION,
      system: this.system,
      task_id: this.taskId,
      t0: this.t0,
      events: this.events,
      ...(route ? { route } : {}),
    };
    const check = validateEnvelope(log, {
      system: this.system,
      issuedAt: this.t0,
      now: Date.now(),
    });
    return { log, valid: check.ok, reason: check.reason };
  }

  private routeLen(): number {
    let len = 0;
    for (let i = 1; i < this.routePts.length; i++) {
      len += Math.hypot(
        this.routePts[i][0] - this.routePts[i - 1][0],
        this.routePts[i][1] - this.routePts[i - 1][1]
      );
    }
    return len;
  }
}
